import { useQuery } from "@tanstack/react-query"
import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { cn } from "design-system/utils"
import { employeesQuery } from "modules/settings/lib/employeesQuery"
import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"

type MentionEntry = { id: string; name: string }

function normalizeStr(str: string) {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function getActiveMention(text: string, cursorPos: number) {
  const textBeforeCursor = text.slice(0, cursorPos)
  const match = textBeforeCursor.match(/@([^\s@]*)$/)
  if (!match || match.index === undefined) return null
  return { start: match.index, query: match[1] ?? "" }
}

function toDisplayText(rawText: string, mentions: MentionEntry[]) {
  const idToName = new Map(mentions.map((m) => [m.id, m.name]))
  return rawText.replace(/@\{#(\d+)\}/g, (_, id) =>
    idToName.has(id) ? `@${idToName.get(id)}` : `@{#${id}}`,
  )
}

function toRawText(displayText: string, mentions: MentionEntry[]) {
  const sorted = [...mentions].sort((a, b) => b.name.length - a.name.length)
  let result = displayText
  for (const { name, id } of sorted) {
    result = result.replace(new RegExp(`@${escapeRegex(name)}`, "g"), `@{#${id}}`)
  }
  return result
}

export function useMentionInput({
  enabled = true,
  onRawChange,
  dropdownPlacement = "below",
}: {
  enabled?: boolean
  onRawChange?: (rawValue: string) => void
  dropdownPlacement?: "above" | "below"
} = {}) {
  const [displayValue, setDisplayValue] = useState("")
  const [localMentions, setLocalMentions] = useState<MentionEntry[]>([])
  const [activeMention, setActiveMention] = useState<{ start: number; query: string } | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const onRawChangeRef = useRef(onRawChange)
  onRawChangeRef.current = onRawChange

  const { data: employees } = useQuery({ ...employeesQuery(), enabled })

  const filteredEmployees =
    activeMention !== null
      ? (employees ?? []).filter((emp) => {
          const name = emp.name ?? emp.email
          return normalizeStr(name).includes(normalizeStr(activeMention.query))
        })
      : []

  const setFromRaw = (raw: string, mentions: MentionEntry[]) => {
    setLocalMentions(mentions)
    setDisplayValue(toDisplayText(raw, mentions))
    setActiveMention(null)
    setSelectedIndex(0)
  }

  const clear = () => {
    setDisplayValue("")
    setLocalMentions([])
    setActiveMention(null)
    setSelectedIndex(0)
  }

  const handleSelectEmployee = (employee: { id: string; name: string | null; email: string }) => {
    if (!activeMention) return
    const displayName = employee.name ?? employee.email
    const replacement = `@${displayName}`
    const before = displayValue.slice(0, activeMention.start)
    const after = displayValue.slice(activeMention.start + 1 + activeMention.query.length)
    const newDisplayValue = before + replacement + after
    const updatedMentions = localMentions.some((m) => m.id === employee.id)
      ? localMentions
      : [...localMentions, { id: employee.id, name: displayName }]

    setDisplayValue(newDisplayValue)
    setActiveMention(null)
    setLocalMentions(updatedMentions)

    onRawChangeRef.current?.(toRawText(newDisplayValue, updatedMentions))

    const newCursor = activeMention.start + replacement.length
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursor, newCursor)
      }
    }, 0)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setDisplayValue(value)
    const cursor = e.target.selectionStart ?? value.length
    const mention = getActiveMention(value, cursor)
    setActiveMention(mention && mention.query ? mention : null)
    setSelectedIndex(0)
    onRawChangeRef.current?.(toRawText(value, localMentions))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!activeMention || filteredEmployees.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, filteredEmployees.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const emp = filteredEmployees[selectedIndex]
      if (emp) handleSelectEmployee(emp)
    } else if (e.key === "Escape") {
      setActiveMention(null)
    }
  }

  const dropdown = (() => {
    if (!activeMention || filteredEmployees.length === 0) return null
    const rect = textareaRef.current?.getBoundingClientRect()
    if (!rect) return null
    return createPortal(
      <div
        className="fixed z-[9999] bg-card border rounded-md shadow-lg overflow-hidden max-h-48 overflow-y-auto"
        style={
          dropdownPlacement === "above"
            ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
            : { top: rect.bottom + 4, left: rect.left, width: rect.width }
        }
      >
        {filteredEmployees.map((emp, i) => (
          <button
            key={emp.id}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              handleSelectEmployee(emp)
            }}
            className={cn(
              "flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors text-left",
              i === selectedIndex && "bg-muted",
            )}
          >
            <Avatar className="h-6 w-6 shrink-0">
              {emp.avatar && <AvatarImage src={emp.avatar} />}
              <AvatarFallback className="text-xs">
                {(emp.name ?? emp.email)[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              {emp.name && <span className="font-medium leading-tight">{emp.name}</span>}
              <span className="text-xs text-muted-foreground">{emp.email}</span>
            </div>
          </button>
        ))}
      </div>,
      document.body,
    )
  })()

  return {
    textareaRef,
    displayValue,
    rawValue: toRawText(displayValue, localMentions),
    setFromRaw,
    clear,
    textareaHandlers: {
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onBlur: () => setActiveMention(null),
    },
    dropdown,
  }
}
